import { Feature } from "ol";
import VectorSource from "ol/source/Vector";
import { MultiPoint, Point } from "ol/geom";
import { DragAndDrop, Draw, Modify } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import Stroke from "ol/style/Stroke";
import { Style, Circle, Fill, Text } from "ol/style";
import { formatArea, formatLength } from "./utils";
import { primaryAction, singleClick } from "ol/events/condition";
import { GeoJSON, GPX, KML } from "ol/format";
import { basic_post_options } from "common/constants";
import { toLonLat } from "ol/proj";
import { styleFunctionMarker } from "./layers";

function getDeleteButtonCoordinates(geometry) {
  let coordinates;
  switch (geometry.getType()) {
    case "Point":
      coordinates = geometry.getCoordinates();
      break;
    case "LineString":
      coordinates = geometry.getCoordinates().pop();
      break;
    case "Polygon":
      coordinates = geometry.getCoordinates()[0].pop();
      break;
    case "MultiPoint":
      coordinates = getDeleteButtonCoordinates(geometry.getPoints().pop());
      break;
    case "MultiLineString":
      coordinates = getDeleteButtonCoordinates(geometry.getLineStrings().pop());
      break;
    case "MultiPolygon":
      coordinates = getDeleteButtonCoordinates(geometry.getPolygons().pop());
      break;
    case "GeometryCollection":
      coordinates = getDeleteButtonCoordinates(geometry.getGeometies().pop());
      break;
    default:
  }
  return coordinates;
}

function getVertexStyles(geometry, active) {
  let styles = [];
  const type = geometry.getType();
  if (type === "GeometryCollection") {
    geometry.getGeometries().forEach((geometry) => {
      styles.push(...getVertexStyles(geometry, active));
    });
  } else if (type.startsWith("Multi")) {
    switch (type) {
      case "MultiLineString":
        geometry.getLineStrings().forEach(geometry => styles.push(...getVertexStyles(geometry, active)));
        break;
      case "MultiPolygon":
        geometry.getPolygons().forEach(geometry => styles.push(...getVertexStyles(geometry, active)));
        break;
      default:
    }
  } else {
    const coords = geometry.getType() === 'Polygon' ? geometry.getCoordinates()[0] : geometry.getType() === 'LineString' ? geometry.getCoordinates() : undefined;
    const tooBig = coords ? coords.length > 100 : false;
    styles = coords ? [ ...coords.reduce((acc, coord, i) => {
      if ((tooBig || !active) && i !== 0 && i !== coords.length - 1) {
        return acc;
      }
      acc.push(new Style({
        zIndex: i === 0 || i === coords.length - 1 ? 1 : 0,
        geometry: new Point(coord),
        image: new Circle({
          radius: i === 0 || i === coords.length - 1 ? 5 : 4,
          stroke: new Stroke({
            width: 2,
            color: i === 0 || i === coords.length - 1 ? 'rgb(230, 50, 15)' : 'white'
          }),
          fill: new Fill({
            color: i === 0 || i === coords.length - 1 ? "white" : "rgb(230, 50, 15)"
          })
        })
      }));
      return acc;
    }, []),
    new Style({
      zIndex: 0,
      geometry: new MultiPoint([coords[0], coords[coords.length - 1]]),
      image: new Circle({
        fill: new Fill({
          color: 'white'
        }),
        radius: 8
      })
    })] : [];
    if (type !== "Point") {
      styles.push(new Style({
        geometry,
        zIndex: 1,
        stroke: new Stroke({
          width: 2,
          color: "rgb(230, 50, 15)"
        }),
        text: new Text({
          font: "700 18px sans-serif",
          text: type === 'Polygon' ? formatArea(geometry) : formatLength(geometry),
          fill: new Fill({
            color: "rgb(230, 50, 15)"
          }),
          stroke: new Stroke({
            width: 4,
            color: 'white'
          }),
          placement: type === 'Polygon' ? undefined : 'line',
          maxAngle: 0,
          offsetY: type === 'Polygon' ? undefined : -10,
          overflow: true,
        })
      }));
    }
  }
  return styles;
}

let hoverHandler;

/**
 * @param {import("ol").Map} map 
 * @param {import("ol").Feature<import("ol/geom/SimpleGeometry").default>} feature 
 * @param {function(Object): void} setHeightData
 * @param {import("react").MutableRefObject<boolean>} showHeightProfile
 */
export function draw(map, feature, setHeightData = () => {}, showHeightProfile = {current: false}) {
  let geometry = feature.getGeometry();
  let type = geometry.getType();
  if (type === 'MultiLineString' && geometry.getLineStrings().length === 1) {
    type = 'LineString';
    geometry = geometry.getLineString(0);
  }
  
  const sketch = new Feature({geometry, type: 'sketch'});

  const deleteButtonCoordinates = getDeleteButtonCoordinates(geometry);
  const deleteButton = new Feature({
    geometry: new Point(deleteButtonCoordinates),
    type: 'button'
  });
  const source = new VectorSource({
    features: [sketch, deleteButton]
  });
  let profileButton;
  if (type === 'LineString') {
    profileButton = new Feature({
      geometry: deleteButton.getGeometry(),
      type: 'button'
    });
    profileButton.setStyle(() => active && new Style({
      zIndex: 2,
      text: new Text({
        font: '24px "Material Icons"',
        text: 'area_chart',
        offsetY: -20,
        offsetX: -10,
        fill: new Fill({
          color: 'rgb(230, 50, 15)'
        }),
        stroke: new Stroke({
          color: '#fff',
          width: 4
        })
      })
    }));
    source.addFeature(profileButton);
  }
  deleteButton.setStyle(() => active &&
    new Style({
      zIndex: 2,
      text: new Text({
        font: '24px "Material Icons"',
        text: 'delete',
        offsetY: type === 'Point' ? 0 : -20,
        offsetX: type === 'Point' ? 15 : profileButton ? 10 : 0,
        fill: new Fill({
          color: 'rgb(230, 50, 15)'
        }),
        stroke: new Stroke({
          color: '#fff',
          width: 4
        })
      })
    })
  );
  geometry.on("change", () => {
    deleteButton.getGeometry().setCoordinates(getDeleteButtonCoordinates(geometry));
  });

  let active = true;
  function renewActive(force = false) {
    if (active) {
      layer.set('inactive', false, true);
      clearTimeout(active);
    }
    if (active || force) {
      modify.setActive(true);
      active = setTimeout(() => {
        active = false;
        modify.setActive(false);
        layer.set('inactive', true);
        layer.changed();
      }, 10000);
    }
  }

  const markerStyle = styleFunctionMarker('place');
  const layer = new VectorLayer({
    properties: {
      type: 'sketch'
    },
    source,
    style: (feature) => {
      const geom = feature.getGeometry();
      const styles = getVertexStyles(geom, active);
      if (geom.getType() === 'Point') {
        styles.push(...markerStyle(feature));
      } else {
        styles.push(
          new Style({
            zIndex: 0,
            stroke: new Stroke({
              width: 6,
              color: 'white',
            })
          }),
        );
      }

      return styles;
    }
  });
  map.addLayer(layer);
  const modify = new Modify({
    source,
    condition: (event) => {
      renewActive();
      return primaryAction(event);
    },
    deleteCondition: (event) => {
      return singleClick(event);
    }
  });
  map.addInteraction(modify);

  function computeHeightProfile(geometry) { 
    if (!showHeightProfile.current) { return } 
    const lonLatCoords = geometry.getCoordinates().map(c => toLonLat(c))
    const height_url_profile = `${process.env.REACT_APP_OVERRIDE_HEIGHT_URL || process.env.PUBLIC_URL + '/api/'}heightProfile`
    const body_2 = { "point_list": lonLatCoords, "steps": 100, "concrete": true };
   // setHeightData({"stats":{"highestPoint":244.12,"lowestPoint":229.61,"heightDiffMinMax":14.51,"heightDiffStartEnd":3.36,"sumUps":29.46,"sumDowns":-26.1,"dist":3036.48},"heights":[232,232.04,232.24,232.19,231.93,231.86,231.31,231.53,231.79,231.75,231.24,231.24,230.72,231.06,230.55,230.45,230.62,230.16,229.69,229.93,229.95,230.02,229.98,230.05,230.05,230.02,230.06,229.91,230.02,229.86,229.88,229.66,229.64,229.65,229.61,230.27,231.13,231.98,232.81,233.25,234.09,235.04,235.88,236.98,238.21,239.37,240.65,241.79,242.65,243.7,244.12,244.04,243.9,243.55,242.92,242.64,242.19,241.78,242.02,241.89,242.07,241.45,241.93,240.8,241.17,240.99,241.11,240.92,241.04,241.53,241.49,241.42,241.46,240.89,241,241.06,240.74,240.62,240.48,240.02,239.57,239.08,238.99,239.48,239.6,239.6,240.88,240.16,239.92,242.71,236.89,237.04,238.23,236.84,237.36,237.93,238.45,240.18,241.75,240.46,235.36],"point_list":[[16.356929075134445,48.17153159979802],[16.357326246576104,48.17145949309543],[16.3577234169008,48.17138738502469],[16.358120586108498,48.17131527558578],[16.35851775419921,48.17124316477875],[16.3589149211729,48.171171052603576],[16.359312087029554,48.17109893906029],[16.359709251769168,48.17102682414886],[16.360106415391716,48.170954707869356],[16.36050357789719,48.17088259022172],[16.360900739285576,48.17081047120598],[16.36129789955685,48.17073835082217],[16.361695058711,48.17066622907027],[16.36209221674802,48.17059410595028],[16.362489373667884,48.170521981462265],[16.36288652947057,48.17044985560616],[16.36328368415608,48.170377728381986],[16.3636808377244,48.1703055997898],[16.364077990175495,48.170233469829554],[16.364475141509374,48.17016133850131],[16.364872291725995,48.17008920580502],[16.36526944082537,48.170017071740666],[16.36566658880746,48.16994493630837],[16.366063735672267,48.16987279950805],[16.366460881419766,48.16980066133972],[16.366858026049943,48.16972852180341],[16.36725516956279,48.16965638089915],[16.367652311958285,48.169584238626896],[16.36804945323641,48.169512094986665],[16.368446593397152,48.16943994997847],[16.36884373244051,48.169367803602356],[16.36924087036644,48.16929565585828],[16.369638007174952,48.16922350674625],[16.370035142866023,48.16915135626632],[16.370432277439647,48.16907920441845],[16.37042610880672,48.168861748130254],[16.37029888379453,48.168600676582344],[16.370171660077634,48.16833960489403],[16.37004443765602,48.168078533065376],[16.369917216529654,48.167817461096305],[16.369789996698522,48.16755638898687],[16.369662778162585,48.16729531673704],[16.369535560921832,48.16703424434686],[16.369408344976236,48.166773171816295],[16.36928113032577,48.16651209914538],[16.3691539169704,48.16625102633409],[16.369026704910116,48.165989953382436],[16.368899494144895,48.165728880290416],[16.368772284674705,48.165467807058015],[16.368645076499533,48.16520673368529],[16.3684553491546,48.16504860359169],[16.36809158929579,48.16517702581442],[16.367727827615422,48.16530544688948],[16.367364064113495,48.16543386681685],[16.367000298790018,48.16556228559649],[16.36663653164498,48.16569070322843],[16.36627276267837,48.16581911971262],[16.365908991890205,48.16594753504911],[16.365545219280484,48.1660759492378],[16.36518144484919,48.16620436227877],[16.364817668596334,48.16633277417193],[16.36445389052191,48.16646118491732],[16.364090110625924,48.16658959451492],[16.363726328908356,48.166718002964714],[16.363362545369235,48.16684641026671],[16.362998760008537,48.166974816420826],[16.362634972826267,48.16710322142713],[16.36227118382244,48.1672316252856],[16.36190739299702,48.16736002799622],[16.361543600350025,48.16748842955895],[16.361179805881466,48.167616829973795],[16.360816009591325,48.16774522924073],[16.360452211479615,48.16787362735978],[16.360088411546315,48.1680020243309],[16.359724609791435,48.16813042015413],[16.359360806214973,48.16825881482939],[16.358997000816938,48.168387208356734],[16.358633193597313,48.16851560073609],[16.358269384556106,48.16864399196747],[16.357905573693323,48.16877238205091],[16.35754176100895,48.1689007709863],[16.357177946502983,48.16902915877375],[16.356814130175433,48.169157545413135],[16.35645031202629,48.169285930904515],[16.356086492055564,48.16941431524786],[16.355722670263237,48.16954269844319],[16.355358846649327,48.16967108049044],[16.35499502121382,48.169799461389616],[16.354631193956717,48.16992784114069],[16.354267364878012,48.170056219743685],[16.35390353397773,48.17018459719863],[16.35353970125584,48.170312973505396],[16.35317586671235,48.17044134866407],[16.35280402102622,48.17048145743086],[16.35241962778087,48.170383281754454],[16.352035236007335,48.1702851047965],[16.351650845705596,48.17018692655697],[16.35126645687569,48.17008874703595],[16.350882069517592,48.16999056623341],[16.350497683631353,48.16989238414937],[16.35010128566056,48.1697911321327]],"bbox":[624329.3061135226,478378.45066041034,625843.5501322398,479065.2009242512]});
    fetch(height_url_profile, { ...basic_post_options, body: JSON.stringify(body_2) })
        .then(r => r.json())
        .then(json => setHeightData(json));

  }

  modify.on('modifyend', (e) => {
    // TODO maybe check if more than two Points on Line
    computeHeightProfile(e.features.getArray().filter(f => f.get('type') !== 'button')[0].getGeometry());
    
  });

  if (!hoverHandler) {
    hoverHandler = (event) => {
      if (event.dragging) {
        return;
      }
      const feature = map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
        if (layer && layer.get('type') === 'sketch' && (layer.get('inactive') || feature.get('type') === 'button')) {
          return feature;
        }
      }, { hitTolerance: 5 });
      if (feature) {
        map.getTargetElement().style.cursor = 'pointer';
      } else {
        map.getTargetElement().style.cursor = '';
      }
    };
    map.on('pointermove', hoverHandler);
  }
  const hitOptions = { hitTolerance: 5, layerFilter: l => l === layer }
  map.on("singleclick", function onclick(evt) {
    const features = map.getFeaturesAtPixel(evt.pixel, hitOptions);
    if (features[0] === deleteButton) {
      map.un("singleclick", onclick);
      map.removeLayer(layer);
      map.removeInteraction(modify);
      setHeightData(null)
    } else if (profileButton && features[0] === profileButton) {
      showHeightProfile.current = true;
      computeHeightProfile(geometry);
    }
    const onSketch = features.includes(sketch);
    const oldActive = active;
    if (onSketch) {
      renewActive(true);  
    }
    if (!!oldActive !== !!active) {
      layer.set('inactive', !active);
      layer.changed();
    }
  });
  renewActive(true);
}

function createFeature(type, map, setHeightData = undefined, showHeightProfile) {
  const drawInteraction = new Draw({
    type,
    stopClick: true,
  });
  drawInteraction.set('mode', type);
  map.addInteraction(drawInteraction);
  drawInteraction.on('drawend', (event) => {
    draw(map, event.feature, setHeightData, showHeightProfile);
    setTimeout(() => map.removeInteraction(drawInteraction), 0);
  });
}

export function drawPoint(map, feature = undefined) {
  const existingDraw = map.getInteractions().getArray().find(i => i instanceof Draw);
  if (existingDraw) {
    map.removeInteraction(existingDraw);
    if (existingDraw.get('mode') === 'Point') {
      return;
    }
  }
  if (!feature) {
    createFeature('Point', map);
    return;
  }
  draw(map, feature);
}

export function drawLine(map, setHeightData, feature = undefined, showHeightProfile = undefined) {
  const existingDraw = map.getInteractions().getArray().find(i => i instanceof Draw && i.get('mode'));
  if (existingDraw) {
    map.removeInteraction(existingDraw);
    if (existingDraw.get('mode') === 'LineString') {
      return;
    }
  }
  if (!feature) {
    createFeature('LineString', map, setHeightData, showHeightProfile);
    return;
  }
  draw(map, feature, setHeightData);
}

export function drawPolygon(map, feature = undefined) {
  const existingDraw = map.getInteractions().getArray().find(i => i instanceof Draw);
  if (existingDraw) {
    map.removeInteraction(existingDraw);
    if (existingDraw.get('mode') === 'Polygon') {
      return;
    }
  }
  if (!feature) {
    createFeature('Polygon', map);
    return;
  }
  draw(map, feature);
}

class KMLNoStyle extends KML {
  constructor(options = {}) {
    options.extractStyles = false;
    super(options);
  }
};

/**
 * @param {import("ol").Map} map 
 */
export function dragAndDrop(map, setHeightData) {
  const source = new VectorSource();
  source.on('addfeature', (event) => {
    draw(map, event.feature, setHeightData);
  });
  const dragDrop = new DragAndDrop({
    formatConstructors: [GeoJSON, GPX, KMLNoStyle],
  });
  map.addInteraction(dragDrop);
  dragDrop.on('addfeatures', (event) => {
    const { features } = event;
    source.addFeatures(features);
    map.getView().fit(source.getExtent(), { duration: 500, padding: [50, 50, 50, 50] });
    source.clear(true);
  });
}

export function eraseAll(map, setHeightData) {
  map.getAllLayers().filter(l => l.get('type') === 'sketch').forEach(l => map.removeLayer(l));
  setHeightData(null);
}